/**
 * PALLET-INTERNAL. The row renderer behind `Select`. Not exported from
 * `index.ts` and never imported by app code; the app reaches its selection
 * helpers via `selectState.MultipleSelection` / `selectState.subsetSelection`.
 */
import { Key } from "@react-types/shared";
import clsx from "clsx";
import {
  ListBoxItem,
  ListBox as RACListBox,
  Selection as RACSelection,
  Text,
} from "react-aria-components";

import Icon from "../icon";
import styles from "./list-box.module.css";

export type Item = { id: Key; label: string; key?: Key };

export type ListBoxSelection<T extends Item = Item> =
  { kind: "empty" } | { kind: "all" } | { kind: "subset"; items: Array<T> };

export const emptySelection: ListBoxSelection<never> = { kind: "empty" };
export const allSelection: ListBoxSelection<never> = { kind: "all" };
export function subsetSelection<T extends Item>(
  items: Array<T>,
): ListBoxSelection<T> {
  return items.length === 0 ? { kind: "empty" } : { kind: "subset", items };
}

interface ListBoxProps<T extends Item> {
  selection?: ListBoxSelection<T>;
  onSelectionChange?: (selection: ListBoxSelection<T>) => void;
  selectionMode?: "single" | "multiple";
  items: Array<T>;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  onScrolledToBottom?: () => void;
  display?: { maxHeight?: string; maxWidth?: string; minWidth?: string };
  className?: string;
}

function isSelected<T extends Item>(
  item: T,
  selection?: ListBoxSelection<T>,
): boolean {
  if (!selection) return false;
  switch (selection.kind) {
    case "empty":
      return false;
    case "all":
      return true;
    case "subset":
      return selection.items.some((s) => s.id === item.id);
  }
}

// ListBox -> RAC. `undefined` stays `undefined` (uncontrolled).
function toRac<T extends Item>(
  selection?: ListBoxSelection<T>,
): "all" | Set<Key> | undefined {
  if (!selection) return undefined;
  switch (selection.kind) {
    case "empty":
      return new Set();
    case "all":
      return "all";
    case "subset":
      return new Set(selection.items.map((i) => i.id));
  }
}

// RAC -> ListBox. Resolve keys against the controlled selection FIRST, then `items`,
// so a pick that is currently filtered out of `items` survives toggling another row.
function fromRac<T extends Item>(
  rac: RACSelection,
  items: Array<T>,
  current?: ListBoxSelection<T>,
): ListBoxSelection<T> {
  if (rac === "all") return { kind: "all" };
  const pool = [...(current?.kind === "subset" ? current.items : []), ...items];
  const picked = Array.from(rac).flatMap(
    (k) => pool.find((i) => i.id === k) ?? [],
  );
  return subsetSelection(picked);
}

const STYLE_DEFAULTS = {
  maxHeight: "400px",
  maxWidth: "400px",
  minWidth: "240px",
};

function ListBox<T extends Item>({
  selection,
  items,
  selectionMode = "single",
  onSelectionChange,
  "aria-labelledby": ariaLabelledby,
  "aria-label": ariaLabel,
  onScrolledToBottom,
  display,
  className,
}: ListBoxProps<T>) {
  const stylesWithDefaults = {
    ...STYLE_DEFAULTS,
    ...display,
  };
  const options = items.map((item) => {
    const itemSelected = isSelected(item, selection);
    return (
      <ListBoxItem
        textValue={item.label}
        id={item.id}
        key={item.key ?? item.id}
        className={clsx(styles.menuItem, { [styles.isSelected]: itemSelected })}
      >
        <Text className={styles.itemLabel} slot="label">
          {item.label}
        </Text>
        <span className={styles.check}>
          <Icon name="Check" data-testid="check-icon" size={18} />
        </span>
      </ListBoxItem>
    );
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (e.target) {
      const t = e.target as HTMLInputElement;
      const bottom =
        Math.round(t.scrollHeight) - Math.round(t.scrollTop) ===
        Math.round(t.clientHeight);
      if (bottom) {
        if (onScrolledToBottom) {
          onScrolledToBottom();
        }
      }
    }
  };

  return (
    <RACListBox
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      selectedKeys={toRac(selection)}
      onSelectionChange={
        onSelectionChange
          ? (rac) => onSelectionChange(fromRac(rac, items, selection))
          : undefined
      }
      className={clsx(styles.list, display, className)}
      style={stylesWithDefaults}
      selectionMode={selectionMode}
      onScroll={handleScroll}
    >
      {options}
    </RACListBox>
  );
}

export default ListBox;
